import { gql } from '@apollo/client';
import { COMPANY_DOCUMENT_FRAGMENT } from './CompanyCreationApi';
import { REVIEW_FRAGMENT } from './ReviewApi';

export const CREATE_TERMINATION_INTAKE_REQUEST = gql`
  mutation CreateTerminationIntakeRequest(
    $createIntakeRequestInputDto: EntityTerminationIntakeRequestInputDtoInput!
  ) {
    createTerminationIntakeRequest(createIntakeRequestInputDto: $createIntakeRequestInputDto) {
      id
      companyCode
    }
  }
`;

export const GET_MONITORING_QUEUE_LIST = gql`
  query MonitoringQueueList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $searchDto: MonitoringQueueSearchDtoInput
    $order: [EntityTerminationListItemDtoSortInput!]
  ) {
    monitoringQueueList(
      first: $first
      after: $after
      last: $last
      before: $before
      searchDto: $searchDto
      order: $order
    ) {
      edges {
        node {
          id
          companyCode
          entityLegalName
          physicalCountry
          entityTerminationStage
          entityStatus
          companyTerminationContacts {
            id
            displayName
          }
          celaTermContact {
            id
            displayName
          }
          businessStakeholder {
            id
            displayName
          }
          statController {
            id
            displayName
          }
          anticipatedTerminationFY
          lifecycleStatus
          terminationLifecycleStatus
          legalInProcessOpenActionItemsCount
          legalInProcessFollowUpRequired
          primaryBlockerCategory
          openBlockersCount
          financeIncompletePercentComplete
          sapDeactivationDate
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
    entityTerminationStageTotals(searchDto: $searchDto) {
      activeIntegratingCount
      activeWindDownCount
      intakeCount
      eligibilityReviewCount
      blockerCount
      legalInProcessCount
      financeIncompleteCount
      eligibleCount
      totalCount
    }
  }
`;

export const GET_ENTITY_TERMINATION_COMPLIANCE_PROCESSES = gql`
  query EntityTerminationComplianceProcesses($companyCode: String!) {
    entityTerminationComplianceProcesses(companyCode: $companyCode) {
      processType
      processTypeId
      onTrack
      late
    }
  }
`;

export const GET_ENTITY_TERMINATION_ELIGIBILITY_REVIEW = gql`
  query EntityTerminationEligibilityReview($companyCode: String!) {
    entityTerminationEligibilityReview(companyCode: $companyCode) {
      reviewId
      terminationLifecycleStatus {
        id
        fieldName
      }
      legoCategory {
        id
        fieldName
      }
      review {
        ...Review
      }
      hasActiveEligibilityReview
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const APPROVE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW = gql`
  mutation ApproveEntityTerminationEligibilityReview(
    $eligibilityReviewInputDto: EligibilityReviewInputDtoInput!
  ) {
    approveEntityTerminationEligibilityReview(
      eligibilityReviewInputDto: $eligibilityReviewInputDto
    ) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const REJECT_ENTITY_TERMINATION_ELIGIBILITY_REVIEW = gql`
  mutation RejectEntityTerminationEligibilityReview(
    $eligibilityReviewInputDto: EligibilityReviewInputDtoInput!
    $lifecycleStatusId: Int!
  ) {
    rejectEntityTerminationEligibilityReview(
      eligibilityReviewInputDto: $eligibilityReviewInputDto
      lifecycleStatusId: $lifecycleStatusId
    ) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const CREATE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW_COMMENT = gql`
  mutation createEntityTerminationEligibilityReviewComment($reviewId: Int!, $comment: String!) {
    createEntityTerminationEligibilityReviewComment(reviewId: $reviewId, comment: $comment) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const ATTACH_ENTITY_TERMINATION_ELIGIBILITY_REVIEW_DOCUMENT = gql`
  mutation attachEntityTerminationEligibilityReviewDocument(
    $reviewDocuments: ReviewDocumentsDtoInput!
  ) {
    attachEntityTerminationEligibilityReviewDocument(reviewDocuments: $reviewDocuments) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const GET_ENTITY_TERMINATION_AGM_STATUS_SUMMARY = gql`
  query EntityTerminationAGMStatusSummary($companyCode: String!) {
    entityTerminationAGMStatusSummary(companyCode: $companyCode) {
      agmStatus
      processCount
    }
  }
`;

export const CREATE_DORMANCY_ASSESSMENT = gql`
  mutation createDormancyAssessment(
    $dormancyAssessmentInput: DormancyAssessmentDtoInput!
    $companyCode: String!
  ) {
    createDormancyAssessment(
      dormancyAssessmentDto: $dormancyAssessmentInput
      companyCode: $companyCode
    ) {
      hasEmployees
      hasRevenue
      hasOperatingExpenses
      mercuryDocumentLink
      comments
    }
  }
`;

export const UPDATE_DORMANCY_ASSESSMENT = gql`
  mutation updateDormancyAssessment(
    $dormancyAssessmentInput: DormancyAssessmentDtoInput!
    $companyCode: String!
  ) {
    updateDormancyAssessment(
      dormancyAssessmentDto: $dormancyAssessmentInput
      companyCode: $companyCode
    ) {
      hasEmployees
      hasRevenue
      hasOperatingExpenses
      mercuryDocumentLink
      comments
    }
  }
`;

export const GET_DORMANCY_ASSESSMENT = gql`
  query DormancyAssessment($companyCode: String!) {
    dormancyAssessment(companyCode: $companyCode) {
      id
      hasEmployees
      hasRevenue
      hasOperatingExpenses
      mercuryDocumentLink
      comments
    }
  }
`;

export const GET_COMPANY_LEGAL_STRUCTURE = gql`
  query CompanyLegalStructure($companyId: Int!) {
    companyLegalStructure(companyId: $companyId) {
      directParentCompany {
        companyCode
        entityLegalName
        physicalCountryCode
        entityStatus {
          fieldName
        }
      }
      directParentOwnershipPercentage
      childrenCompanies {
        companyCode
        entityLegalName
        physicalCountryCode
        entityStatus {
          fieldName
        }
        terminationLifecycleStatus {
          fieldName
        }
        directParentCompany {
          companyCode
          entityLegalName
        }
        directParentOwnershipPercentage
      }
      terminatedSubsidiariesCount
      nonTerminatedSubsidiariesCount
      noticesPendingSubsidiariesCount
      activeSubsidiariesCount
    }
  }
`;

const ENTITY_TERMINATION_USER_FRAGMENT = gql`
  fragment EntityTerminationUser on ServiceContactDto {
    id
    graphId
    text: name
    name
    firstName
    lastName
    email
    alias
    isInvalidUser
    personnelNumber
  }
`;

const LEGAL_IN_PROCESS_ACTION_ITEM_SUMMARY_FRAGMENT = gql`
  fragment LegalInProcessActionItemSummaryFields on LegalInProcessActionItemDto {
    id
    description
    owner {
      ...EntityTerminationUser
    }
    dateStarted
    dateCompleted
    status
  }
`;

const LEGAL_IN_PROCESS_ACTION_ITEM_TASK_CORE_FRAGMENT = gql`
  fragment LegalInProcessActionItemTaskCoreFields on LegalInProcessActionItemTaskDto {
    description
    owner {
      ...EntityTerminationUser
    }
    comment
    assignees {
      ...EntityTerminationUser
    }
    response
    followUpFlagDate
    completedDate
  }
`;

const LEGAL_IN_PROCESS_ACTION_ITEM_TASK_FRAGMENT = gql`
  fragment LegalInProcessActionItemTaskFields on LegalInProcessActionItemTaskDto {
    id
    ...LegalInProcessActionItemTaskCoreFields
    taskHistories {
      id
      actionType
      actionDate
      updatedValue
      changedBy {
        displayName
      }
    }
  }
`;

const LEGAL_IN_PROCESS_ACTION_ITEM_DETAILS_FRAGMENT = gql`
  fragment LegalInProcessActionItemDetailsFields on LegalInProcessActionItemDto {
    id
    companyCode
    entityLegalName
    description
    owner {
      ...EntityTerminationUser
    }
    dateStarted
    dateCompleted
    status
    tasks {
      ...LegalInProcessActionItemTaskFields
    }
  }
`;

const LEGAL_IN_PROCESS_ACTION_ITEM_WITH_TASKS_FRAGMENT = gql`
  fragment LegalInProcessActionItemWithTasksFields on LegalInProcessActionItemDto {
    id
    description
    owner {
      ...EntityTerminationUser
    }
    dateStarted
    dateCompleted
    status
    tasks {
      ...LegalInProcessActionItemTaskFields
    }
  }
`;

export const GET_LEGAL_IN_PROCESS_REVIEW = gql`
  query LegalInProcessReview($companyCode: String!) {
    legalInProcessReview(companyCode: $companyCode) {
      actionItems {
        ...LegalInProcessActionItemSummaryFields
        tasks {
          id
          completedDate
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_SUMMARY_FRAGMENT}
`;

export const GET_LEGAL_IN_PROCESS_ACTION_ITEM = gql`
  query LegalInProcessActionItem($actionItemId: Int!) {
    actionItem: legalInProcessActionItem(actionItemId: $actionItemId) {
      ...LegalInProcessActionItemDetailsFields
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_CORE_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_DETAILS_FRAGMENT}
`;

export const CREATE_LEGAL_IN_PROCESS_ACTION_ITEM = gql`
  mutation CreateLegalInProcessActionItem(
    $companyCode: String!
    $legalInProcessActionItemDto: LegalInProcessActionItemDtoInput!
  ) {
    createLegalInProcessActionItem(
      companyCode: $companyCode
      legalInProcessActionItemDto: $legalInProcessActionItemDto
    ) {
      actionItems {
        ...LegalInProcessActionItemSummaryFields
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_SUMMARY_FRAGMENT}
`;

export const UPDATE_LEGAL_IN_PROCESS_ACTION_ITEM = gql`
  mutation UpdateLegalInProcessActionItem(
    $companyCode: String!
    $legalInProcessActionItemDto: LegalInProcessActionItemDtoInput!
  ) {
    updateLegalInProcessActionItem(
      companyCode: $companyCode
      legalInProcessActionItemDto: $legalInProcessActionItemDto
    ) {
      actionItems {
        ...LegalInProcessActionItemWithTasksFields
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_CORE_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_WITH_TASKS_FRAGMENT}
`;

export const CREATE_LEGAL_IN_PROCESS_ACTION_ITEM_TASK = gql`
  mutation CreateLegalInProcessActionItemTask(
    $companyCode: String!
    $legalInProcessActionItemTaskDto: LegalInProcessActionItemTaskDtoInput!
  ) {
    createLegalInProcessActionItemTask(
      companyCode: $companyCode
      legalInProcessActionItemTaskDto: $legalInProcessActionItemTaskDto
    ) {
      ...LegalInProcessActionItemTaskCoreFields
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_CORE_FRAGMENT}
`;

export const UPDATE_LEGAL_IN_PROCESS_ACTION_ITEM_TASK = gql`
  mutation UpdateLegalInProcessActionItemTask(
    $companyCode: String!
    $legalInProcessActionItemTaskDto: LegalInProcessActionItemTaskDtoInput!
  ) {
    updateLegalInProcessActionItemTask(
      companyCode: $companyCode
      legalInProcessActionItemTaskDto: $legalInProcessActionItemTaskDto
    ) {
      ...LegalInProcessActionItemTaskFields
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_CORE_FRAGMENT}
  ${LEGAL_IN_PROCESS_ACTION_ITEM_TASK_FRAGMENT}
`;

export const UPDATE_LEGAL_IN_PROCESS_DETAILS = gql`
  mutation updateLegalInProcessDetails(
    $companyCode: String!
    $input: LegalInProcessDetailsDtoInput!
  ) {
    updateLegalInProcessDetails(companyCode: $companyCode, input: $input) {
      id
      accountingPlanStarted
    }
  }
`;

export const GET_FINANCE_INCOMPLETE_DETAILS = gql`
  query FinanceIncompleteDetails($companyCode: String!) {
    financeIncompleteDetails(companyCode: $companyCode) {
      companyCode
      actionItems {
        id
        entityLegalName
        description
        owner {
          ...EntityTerminationUser
        }
        dateStarted
        dateCompleted
        status
        tasks {
          id
          description
          isTemplateTask
          completedDate
          documents {
            id
          }
        }
      }
      progress {
        completedTaskCount
        totalTaskCount
        percentComplete
        sapDeactivationDate
        actionItemsProgress {
          actionItemId
          description
          completedTaskCount
          totalTaskCount
          percentComplete
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const GET_FINANCE_INCOMPLETE_ACTION_ITEM = gql`
  query FinanceIncompleteActionItem($companyCode: String!, $actionItemId: Int!) {
    actionItem: financeIncompleteActionItem(
      companyCode: $companyCode
      actionItemId: $actionItemId
    ) {
      id
      companyCode
      entityLegalName
      description
      owner {
        ...EntityTerminationUser
      }
      dateStarted
      dateCompleted
      status
      tasks {
        id
        description
        isTemplateTask
        owner {
          ...EntityTerminationUser
        }
        comment
        assignees {
          ...EntityTerminationUser
        }
        response
        followUpFlagDate
        completedDate
        documents {
          ...CompanyDocument
        }
        taskHistories {
          id
          actionType
          actionDate
          updatedValue
          changedBy {
            displayName
          }
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${COMPANY_DOCUMENT_FRAGMENT}
`;

export const UPDATE_FINANCE_INCOMPLETE_ACTION_ITEM = gql`
  mutation UpdateFinanceIncompleteActionItem(
    $companyCode: String!
    $financeIncompleteActionItemDto: FinanceIncompleteActionItemDtoInput!
  ) {
    updateFinanceIncompleteActionItem(
      companyCode: $companyCode
      financeIncompleteActionItemDto: $financeIncompleteActionItemDto
    ) {
      id
      companyCode
      entityLegalName
      description
      owner {
        ...EntityTerminationUser
      }
      dateStarted
      dateCompleted
      status
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const CREATE_FINANCE_INCOMPLETE_ACTION_ITEM_TASK = gql`
  mutation CreateFinanceIncompleteActionItemTask(
    $companyCode: String!
    $financeIncompleteTaskDto: FinanceIncompleteTaskInputDtoInput!
  ) {
    createFinanceIncompleteActionItemTask(
      companyCode: $companyCode
      financeIncompleteTaskDto: $financeIncompleteTaskDto
    ) {
      id
      financeIncompleteActionItemId
      description
      isTemplateTask
      owner {
        ...EntityTerminationUser
      }
      comment
      assignees {
        ...EntityTerminationUser
      }
      response
      followUpFlagDate
      completedDate
      taskHistories {
        id
        actionType
        actionDate
        updatedValue
        changedBy {
          displayName
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const UPDATE_FINANCE_INCOMPLETE_ACTION_ITEM_TASK = gql`
  mutation UpdateFinanceIncompleteActionItemTask(
    $companyCode: String!
    $financeIncompleteTaskDto: FinanceIncompleteTaskInputDtoInput!
  ) {
    updateFinanceIncompleteActionItemTask(
      companyCode: $companyCode
      financeIncompleteTaskDto: $financeIncompleteTaskDto
    ) {
      id
      financeIncompleteActionItemId
      description
      owner {
        ...EntityTerminationUser
      }
      comment
      assignees {
        ...EntityTerminationUser
      }
      response
      followUpFlagDate
      completedDate
      taskHistories {
        id
        actionType
        actionDate
        updatedValue
        changedBy {
          displayName
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const GET_BLOCKER_QUEUE_REVIEW = gql`
  query BlockerQueueReview($companyCode: String!) {
    blockerQueueReview(companyCode: $companyCode) {
      blockerQueueActionItems {
        id
        companyCode
        legoCategory {
          id
          fieldName
        }
        description
        owner {
          ...EntityTerminationUser
        }
        dateStarted
        dateCompleted
        isPrimaryBlocker
      }
      reviewId
      terminationLifecycleStatus {
        id
        fieldName
      }
      review {
        ...Review
      }
      hasActiveBlockerReview
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
  ${REVIEW_FRAGMENT}
`;

export const GET_BLOCKER_QUEUE_ACTION_ITEM = gql`
  query BlockerQueueActionItem($actionItemId: Int!) {
    actionItem: blockerQueueActionItem(actionItemId: $actionItemId) {
      id
      companyCode
      entityLegalName
      description
      legoCategory {
        id
        fieldName
      }
      owner {
        ...EntityTerminationUser
      }
      dateStarted
      dateCompleted
      status
      isPrimaryBlocker
      tasks {
        id
        description
        owner {
          ...EntityTerminationUser
        }
        comment
        assignees {
          ...EntityTerminationUser
        }
        response
        followUpFlagDate
        completedDate
        taskHistories {
          id
          actionType
          actionDate
          updatedValue
          changedBy {
            displayName
          }
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const CREATE_BLOCKER_QUEUE_ACTION_ITEM = gql`
  mutation CreateBlockerQueueActionItem(
    $companyCode: String!
    $blockerQueueActionItemDto: BlockerQueueActionItemDtoInput!
  ) {
    createBlockerQueueActionItem(
      companyCode: $companyCode
      blockerQueueActionItemDto: $blockerQueueActionItemDto
    ) {
      blockerQueueActionItems {
        id
        companyCode
        legoCategory {
          id
          fieldName
        }
        description
        owner {
          ...EntityTerminationUser
        }
        dateStarted
        dateCompleted
        status
        isPrimaryBlocker
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const UPDATE_BLOCKER_QUEUE_ACTION_ITEM = gql`
  mutation UpdateBlockerQueueActionItem(
    $companyCode: String!
    $blockerQueueActionItemDto: BlockerQueueActionItemDtoInput!
  ) {
    updateBlockerQueueActionItem(
      companyCode: $companyCode
      blockerQueueActionItemDto: $blockerQueueActionItemDto
    ) {
      blockerQueueActionItems {
        id
        companyCode
        legoCategory {
          id
          fieldName
        }
        description
        owner {
          ...EntityTerminationUser
        }
        dateStarted
        dateCompleted
        status
        isPrimaryBlocker
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const GET_ENTITY_TERMINATION_OVERVIEW = gql`
  query EntityTerminationOverview($companyCode: String!) {
    entityTerminationOverview(companyCode: $companyCode) {
      country
      functionalCurrency
      statutoryReportingCurrency
      statutoryYearEnd
      auditor {
        id
        fieldName
      }
      reportingGroup {
        id
        fieldName
      }
      legalStatus {
        id
        fieldName
      }
      lifecycleStatus {
        id
        fieldName
      }
      terminationLifecycleStatus {
        id
        fieldName
      }
      blockerCategory {
        id
        fieldName
      }
      intakeMethod {
        id
        fieldName
      }
      anticipatedTerminationFY
      anticipatedTerminationQuarter
      terminationContacts {
        id
        graphId
        text: name
      }
      celaTermContact {
        id
        graphId
        text: name
      }
      businessStakeholder {
        id
        graphId
        text: name
      }
      statutoryController {
        id
        graphId
        text: name
      }
      generalComments
      bankAccount
      ihcc
      uscc
      loan
      hasPrimaryBlocker
    }
  }
`;

export const UPDATE_ENTITY_TERMINATION_OVERVIEW_STATUS_INFO = gql`
  mutation UpdateOverviewStatusInfo(
    $companyCode: String!
    $updateOverviewStatusInfoDto: UpdateOverviewStatusInfoDtoInput!
  ) {
    updateOverviewStatusInfo(
      companyCode: $companyCode
      updateOverviewStatusInfoDto: $updateOverviewStatusInfoDto
    ) {
      legalStatus {
        id
        fieldName
      }
      lifecycleStatus {
        id
        fieldName
      }
      terminationLifecycleStatus {
        id
        fieldName
      }
      blockerCategory {
        id
        fieldName
      }
      intakeMethod {
        id
        fieldName
      }
      anticipatedTerminationFY
      anticipatedTerminationQuarter
    }
  }
`;

export const UPDATE_ENTITY_TERMINATION_OVERVIEW_CONTACTS = gql`
  mutation UpdateOverviewContacts(
    $companyCode: String!
    $updateOverviewContactsDto: UpdateOverviewContactsDtoInput!
  ) {
    updateOverviewContacts(
      companyCode: $companyCode
      updateOverviewContactsDto: $updateOverviewContactsDto
    ) {
      terminationContacts {
        id
        graphId
        text: name
      }
      celaTermContact {
        id
        graphId
        text: name
      }
      businessStakeholder {
        id
        graphId
        text: name
      }
      statutoryController {
        id
        graphId
        text: name
      }
      generalComments
    }
  }
`;

export const UPDATE_ENTITY_TERMINATION_OVERVIEW_TREASURY = gql`
  mutation UpdateOverviewTreasury(
    $companyCode: String!
    $updateOverviewTreasuryDto: UpdateOverviewTreasuryDtoInput!
  ) {
    updateOverviewTreasury(
      companyCode: $companyCode
      updateOverviewTreasuryDto: $updateOverviewTreasuryDto
    ) {
      bankAccount
      ihcc
      uscc
      loan
    }
  }
`;

export const CREATE_BLOCKER_TASK = gql`
  mutation CreateBlockerQueueTask(
    $companyCode: String!
    $blockerQueueActionItemTaskDto: BlockerQueueActionItemTaskDtoInput!
  ) {
    createBlockerQueueTask(
      companyCode: $companyCode
      blockerQueueActionItemTaskDto: $blockerQueueActionItemTaskDto
    ) {
      id
      description
      owner {
        ...EntityTerminationUser
      }
      comment
      assignees {
        ...EntityTerminationUser
      }
      response
      followUpFlagDate
      completedDate
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const UPDATE_BLOCKER_TASK = gql`
  mutation UpdateBlockerQueueTask(
    $companyCode: String!
    $blockerQueueActionItemTaskDto: BlockerQueueActionItemTaskDtoInput!
  ) {
    updateBlockerQueueTask(
      companyCode: $companyCode
      blockerQueueActionItemTaskDto: $blockerQueueActionItemTaskDto
    ) {
      id
      description
      owner {
        ...EntityTerminationUser
      }
      comment
      assignees {
        ...EntityTerminationUser
      }
      response
      followUpFlagDate
      completedDate
      taskHistories {
        id
        actionType
        actionDate
        updatedValue
        changedBy {
          displayName
        }
      }
    }
  }
  ${ENTITY_TERMINATION_USER_FRAGMENT}
`;

export const GET_ENTITY_TERMINATION_LEGAL_REVIEW = gql`
  query EntityTerminationLegalReview($companyCode: String!) {
    entityTerminationLegalReview(companyCode: $companyCode) {
      reviewId
      entityStatus {
        id
        fieldName
      }
      terminationLifecycleStatus {
        id
        fieldName
      }
      review {
        ...Review
      }
      hasActiveLegalReview
      noticesPendingDate
      notificationDate
      legalTerminationType {
        id
        fieldName
      }
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const CREATE_ENTITY_TERMINATION_LEGAL_REVIEW_COMMENT = gql`
  mutation createEntityTerminationLegalReviewComment($reviewId: Int!, $comment: String!) {
    createEntityTerminationLegalReviewComment(reviewId: $reviewId, comment: $comment) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const ATTACH_ENTITY_TERMINATION_LEGAL_REVIEW_DOCUMENT = gql`
  mutation attachEntityTerminationLegalReviewDocument($reviewDocuments: ReviewDocumentsDtoInput!) {
    attachEntityTerminationLegalReviewDocument(reviewDocuments: $reviewDocuments) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const APPROVE_ENTITY_TERMINATION_BLOCKER_REVIEW = gql`
  mutation ApproveEntityTerminationBlockerReview(
    $blockerReviewInputDto: BlockerReviewInputDtoInput!
  ) {
    approveEntityTerminationBlockerReview(blockerReviewInputDto: $blockerReviewInputDto) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const CREATE_ENTITY_TERMINATION_BLOCKER_REVIEW_COMMENT = gql`
  mutation createEntityTerminationBlockerReviewComment($reviewId: Int!, $comment: String!) {
    createEntityTerminationBlockerReviewComment(reviewId: $reviewId, comment: $comment) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const ATTACH_ENTITY_TERMINATION_BLOCKER_REVIEW_DOCUMENT = gql`
  mutation attachEntityTerminationBlockerReviewDocument(
    $reviewDocuments: ReviewDocumentsDtoInput!
  ) {
    attachEntityTerminationBlockerReviewDocument(reviewDocuments: $reviewDocuments) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const GET_FINANCE_INCOMPLETE_OPEN_PROCESSES = gql`
  query FinanceIncompleteOpenProcesses($companyCode: String!) {
    financeIncompleteOpenProcesses(companyCode: $companyCode) {
      annualFinancialStatementCount
      directTaxCount
      otherTaxCount
      otherProcessesCount
      annualFinancialStatementProcessTypeIds
      directTaxProcessTypeIds
      otherTaxProcessTypeIds
      otherProcessesProcessTypeIds
    }
  }
`;

export const GET_FINANCE_INCOMPLETE_REVIEW = gql`
  query FinanceIncompleteReview($companyCode: String!) {
    financeIncompleteReview(companyCode: $companyCode) {
      reviewId
      review {
        ...Review
      }
      hasActiveFinanceIncompleteReview
      legalTerminationType {
        id
        fieldName
      }
      hasMatchingLifecycleStatus
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const APPROVE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW = gql`
  mutation ApproveEntityTerminationFinanceIncompleteReview(
    $financeIncompleteReviewInputDto: FinanceIncompleteReviewInputDtoInput!
  ) {
    approveEntityTerminationFinanceIncompleteReview(
      financeIncompleteReviewInputDto: $financeIncompleteReviewInputDto
    ) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const CREATE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW_COMMENT = gql`
  mutation createEntityTerminationFinanceIncompleteReviewComment(
    $reviewId: Int!
    $comment: String!
  ) {
    createEntityTerminationFinanceIncompleteReviewComment(reviewId: $reviewId, comment: $comment) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const ATTACH_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW_DOCUMENT = gql`
  mutation attachEntityTerminationFinanceIncompleteReviewDocument(
    $reviewDocuments: ReviewDocumentsDtoInput!
  ) {
    attachEntityTerminationFinanceIncompleteReviewDocument(reviewDocuments: $reviewDocuments) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const UPDATE_ENTITY_TERMINATION_BLOCKER_DOCUMENTS = gql`
  mutation updateEntityTerminationBlockerDocuments(
    $input: EntityTerminationDocumentsInputDtoInput!
  ) {
    updateEntityTerminationBlockerDocuments(input: $input) {
      id
      terminationDocuments {
        ...CompanyDocument
      }
      terminationLegalReviewDocuments {
        ...CompanyDocument
      }
    }
  }
  ${COMPANY_DOCUMENT_FRAGMENT}
`;

export const UPDATE_ENTITY_TERMINATION_LEGAL_REVIEW_DOCUMENTS = gql`
  mutation updateEntityTerminationLegalReviewDocuments(
    $input: EntityTerminationDocumentsInputDtoInput!
  ) {
    updateEntityTerminationLegalReviewDocuments(input: $input) {
      id
      terminationDocuments {
        ...CompanyDocument
      }
      terminationLegalReviewDocuments {
        ...CompanyDocument
      }
    }
  }
  ${COMPANY_DOCUMENT_FRAGMENT}
`;

export const GET_LEGAL_TERMINATION_TYPES = gql`
  query LegalTerminationTypes {
    legalTerminationTypes {
      id
      fieldName
    }
  }
`;

export const MOVE_ENTITY_TERMINATION_TO_BLOCKER = gql`
  mutation MoveEntityTerminationToBlocker($input: MoveToBlockerInputDtoInput!) {
    moveEntityTerminationToBlocker(input: $input) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const MOVE_ENTITY_TERMINATION_TO_NOTICES_PENDING = gql`
  mutation MoveEntityTerminationToNoticesPending($input: MoveToNoticesPendingInputDtoInput!) {
    moveEntityTerminationToNoticesPending(input: $input) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;

export const FINALIZE_ENTITY_TERMINATION_LEGAL_REVIEW = gql`
  mutation FinalizeEntityTerminationLegalReview(
    $input: FinalizeEntityTerminationLegalReviewInputDtoInput!
  ) {
    finalizeEntityTerminationLegalReview(input: $input) {
      ...Review
    }
  }
  ${REVIEW_FRAGMENT}
`;
